<template>
  <div class="efd-wrapper">
    <!-- Topbar -->
    <div class="efd-topbar">
      <button
        class="efd-back-btn"
        @click="goBack"
      >
        <i class="fa fa-arrow-left" />
        <span>Volver</span>
      </button>
      <div class="efd-topbar-title">
        <h2>{{ focusName || 'Resultados de Ejecución' }}</h2>
        <p>Visitas registradas por el equipo de campo</p>
      </div>
    </div>

    <!-- Skeleton -->
    <div
      v-if="loading"
      class="efd-card"
    >
      <div class="efd-skeleton-rows">
        <div
          v-for="n in 6"
          :key="n"
          class="efd-skeleton-row"
        >
          <div class="efd-skel efd-skel--long shimmer" />
          <div class="efd-skel efd-skel--med shimmer" />
          <div class="efd-skel efd-skel--med shimmer" />
          <div class="efd-skel efd-skel--short shimmer" />
        </div>
      </div>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="efd-error"
    >
      <i class="fa fa-exclamation-circle" />
      {{ error }}
      <button
        class="efd-retry-btn"
        @click="load"
      >
        Reintentar
      </button>
    </div>

    <!-- Tabla de visitas -->
    <div
      v-else
      class="efd-card"
    >
      <b-table
        :items="visits"
        :fields="fields"
        class="efd-table"
        thead-class="efd-thead"
        tbody-class="efd-tbody"
        responsive
        hover
        show-empty
        empty-text="No hay visitas registradas para este foco."
        @row-clicked="openVisit"
      >
        <template #cell(submitted_at)="{ item }">
          {{ formatDatetime(item.submittedAt) }}
        </template>
        <template #cell(store)="{ item }">
          <span class="efd-store-name">{{ item.storeName }}</span>
          <span
            v-if="item.storeCode"
            class="efd-store-code"
          >{{ item.storeCode }}</span>
        </template>
        <template #cell(user)="{ item }">
          {{ item.userName }}
        </template>
        <template #cell(product)="{ item }">
          {{ item.productName }}
        </template>
        <template #cell(detail)>
          <span class="efd-detail-hint">
            <i class="fa fa-chevron-right" />
          </span>
        </template>
      </b-table>
    </div>

    <!-- Panel de detalle -->
    <transition name="efd-slide">
      <div
        v-if="selectedVisit"
        class="efd-panel-overlay"
        @click.self="closePanel"
      >
        <div class="efd-panel">
          <div class="efd-panel-header">
            <div>
              <h3 class="efd-panel-title">
                Detalle de visita
              </h3>
              <p class="efd-panel-sub">
                {{ selectedVisit.productName }} ·
                {{ selectedVisit.storeName }} ·
                {{ formatDatetime(selectedVisit.submittedAt) }}
              </p>
            </div>
            <button
              class="efd-panel-close"
              @click="closePanel"
            >
              <i class="fa fa-times" />
            </button>
          </div>

          <div class="efd-panel-body">
            <!-- Cargando detalle -->
            <div
              v-if="detailLoading"
              class="efd-detail-loading"
            >
              <div
                v-for="n in 4"
                :key="n"
                class="efd-skel efd-skel--full shimmer"
                style="margin-bottom:12px;height:56px;border-radius:8px"
              />
            </div>

            <!-- Reglas -->
            <template v-else-if="detail">
              <div
                v-if="!detail.rules.length"
                class="efd-no-rules"
              >
                Sin respuestas registradas
              </div>
              <div
                v-for="rule in detail.rules"
                :key="rule.ruleTypeId"
                class="efd-rule-row"
              >
                <div class="efd-rule-info">
                  <span class="efd-rule-name">{{ ruleMeta(rule.ruleTypeId).name }}</span>
                  <span class="efd-rule-type-badge">{{ ruleMeta(rule.ruleTypeId).inputType }}</span>
                </div>

                <!-- boolean -->
                <template v-if="ruleMeta(rule.ruleTypeId).inputType === 'boolean'">
                  <span
                    v-if="rule.value === 'true'"
                    class="efd-bool efd-bool--yes"
                  >
                    <i class="fa fa-check" /> Sí
                  </span>
                  <span
                    v-else-if="rule.value === 'false'"
                    class="efd-bool efd-bool--no"
                  >
                    <i class="fa fa-times" /> No
                  </span>
                  <span
                    v-else
                    class="efd-null"
                  >—</span>
                </template>

                <!-- photo -->
                <template v-else-if="ruleMeta(rule.ruleTypeId).inputType === 'photo'">
                  <div
                    v-if="rule.photoUrl !== null"
                    class="efd-photo-area"
                  >
                    <img
                      v-if="photoUrls[rule.ruleTypeId]"
                      :src="photoUrls[rule.ruleTypeId]"
                      class="efd-photo-img"
                      alt="foto"
                    >
                    <button
                      v-else
                      class="efd-photo-btn"
                      :disabled="photoLoading[rule.ruleTypeId]"
                      @click="loadPhoto(rule.ruleTypeId)"
                    >
                      <i
                        class="fa"
                        :class="photoLoading[rule.ruleTypeId] ? 'fa-circle-o-notch fa-spin' : 'fa-camera'"
                      />
                      Ver foto
                    </button>
                  </div>
                  <span
                    v-else
                    class="efd-null"
                  >Sin foto</span>
                </template>

                <!-- text / number -->
                <template v-else>
                  <span
                    v-if="rule.value !== null && rule.value !== undefined"
                    class="efd-value"
                  >{{ rule.value }}</span>
                  <span
                    v-else
                    class="efd-null"
                  >—</span>
                </template>

                <div
                  v-if="rule.comment"
                  class="efd-comment"
                >
                  <i class="fa fa-comment-o" />
                  {{ rule.comment }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { getExecutionFocus, getExecutionFocusVisits, getExecutionFocusVisit, getExecutionFocusVisitPhoto } from '../Services'
import ExecutionFocusVisit from '../models/ExecutionFocusVisit'
import ExecutionFocusVisitDetail from '../models/ExecutionFocusVisitDetail'

export default {
  name: 'ExecutionFocusDashboard',
  props: {
    id: { type: [String, Number], required: true }
  },
  setup (props, context) {
    const loading = ref(false)
    const error = ref(null)
    const focusName = ref('')
    const visits = ref([])
    const ruleMap = ref({})

    const selectedVisit = ref(null)
    const detail = ref(null)
    const detailLoading = ref(false)
    const photoUrls = ref({})
    const photoLoading = ref({})

    const fields = [
      { key: 'store', label: 'Tienda', sortable: false },
      { key: 'user', label: 'Usuario', sortable: false },
      { key: 'product', label: 'Producto', sortable: false },
      { key: 'submitted_at', label: 'Fecha', sortable: true },
      { key: 'detail', label: '', thStyle: { width: '40px' } }
    ]

    const ruleMeta = computed(() => (ruleTypeId) => {
      return ruleMap.value[ruleTypeId] || { name: `Regla ${ruleTypeId}`, inputType: 'text' }
    })

    const load = async () => {
      loading.value = true
      error.value = null
      try {
        const [focusData, visitsData] = await Promise.all([
          getExecutionFocus(props.id),
          getExecutionFocusVisits(props.id)
        ])
        focusName.value = focusData.name || ''
        const map = {}
        if (Array.isArray(focusData.products)) {
          focusData.products.forEach(p => {
            if (Array.isArray(p.rules)) {
              p.rules.forEach(r => {
                map[r.rule_type_id] = { name: r.rule_name || r.question_label || `Regla ${r.rule_type_id}`, inputType: r.input_type }
              })
            }
          })
        }
        ruleMap.value = map
        visits.value = Array.isArray(visitsData)
          ? visitsData.map(v => new ExecutionFocusVisit(v))
          : []
      } catch (e) {
        error.value = e.message || 'Error al cargar los resultados'
      } finally {
        loading.value = false
      }
    }

    const openVisit = async (visit) => {
      selectedVisit.value = visit
      detail.value = null
      photoUrls.value = {}
      photoLoading.value = {}
      detailLoading.value = true
      try {
        const raw = await getExecutionFocusVisit(visit.visitId)
        detail.value = new ExecutionFocusVisitDetail(raw)
      } catch {
        detail.value = null
      } finally {
        detailLoading.value = false
      }
    }

    const closePanel = () => {
      selectedVisit.value = null
      detail.value = null
      photoUrls.value = {}
      photoLoading.value = {}
    }

    const loadPhoto = async (ruleTypeId) => {
      if (photoLoading.value[ruleTypeId] || !detail.value) return
      context.root.$set(photoLoading.value, ruleTypeId, true)
      try {
        const data = await getExecutionFocusVisitPhoto(detail.value.visitId, ruleTypeId)
        context.root.$set(photoUrls.value, ruleTypeId, data.url)
      } catch {
        // silencioso
      } finally {
        context.root.$set(photoLoading.value, ruleTypeId, false)
      }
    }

    const formatDatetime = (dt) => {
      if (!dt) return '—'
      const d = new Date(dt)
      if (isNaN(d.getTime())) return dt
      return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }

    const goBack = () => context.root.$router.go(-1)

    onMounted(load)

    return {
      loading,
      error,
      focusName,
      visits,
      fields,
      selectedVisit,
      detail,
      detailLoading,
      photoUrls,
      photoLoading,
      ruleMeta,
      load,
      openVisit,
      closePanel,
      loadPhoto,
      formatDatetime,
      goBack
    }
  }
}
</script>

<style scoped>
.efd-wrapper {
  padding: 2rem 1rem 3rem;
  max-width: 960px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* Topbar */
.efd-topbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.efd-back-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
  font-size: 0.85rem;
  color: #4a5568;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.efd-back-btn:hover { background: #f7fafc; }

.efd-topbar-title h2 {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
  color: #2d3748;
}

.efd-topbar-title p {
  margin: 0;
  font-size: 0.85rem;
  color: #718096;
}

/* Card */
.efd-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  overflow: hidden;
}

/* Skeleton */
.efd-skeleton-rows { padding: 0; }

.efd-skeleton-row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f7fafc;
}

@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 4px;
  flex-shrink: 0;
}

.efd-skel { height: 0.85rem; }
.efd-skel--short { width: 70px; }
.efd-skel--med   { width: 130px; }
.efd-skel--long  { width: 180px; }
.efd-skel--full  { width: 100%; }

/* Table */
.efd-table {
  margin: 0;
  border: none;
}

.efd-table >>> .efd-thead th {
  background: #f7fafc;
  border: none;
  color: #4a5568;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.05em;
  padding: 0.875rem 1.25rem;
}

.efd-table >>> .efd-tbody td {
  border: none;
  padding: 0.875rem 1.25rem;
  vertical-align: middle;
  border-bottom: 1px solid #edf2f7;
  font-size: 0.875rem;
  color: #2d3748;
  cursor: pointer;
}

.efd-table >>> .efd-tbody tr:last-child td { border-bottom: none; }
.efd-table >>> .efd-tbody tr:hover { background: #f8fafc; }

.efd-store-name { display: block; font-weight: 500; }
.efd-store-code { display: block; font-size: 0.75rem; color: #a0aec0; }

.efd-detail-hint {
  color: #a0aec0;
  font-size: 0.75rem;
}

/* Error */
.efd-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  background: rgba(229,62,62,.08);
  border: 1px solid rgba(229,62,62,.2);
  border-radius: 8px;
  color: #c53030;
  font-size: 0.875rem;
}

.efd-retry-btn {
  margin-left: auto;
  background: none;
  border: 1px solid #c53030;
  color: #c53030;
  border-radius: 6px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
}

.efd-retry-btn:hover { background: #c53030; color: white; }

/* Panel overlay */
.efd-panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.efd-panel {
  width: 420px;
  max-width: 95vw;
  height: 100%;
  background: white;
  box-shadow: -4px 0 24px rgba(0,0,0,.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.efd-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #edf2f7;
}

.efd-panel-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
}

.efd-panel-sub {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #718096;
}

.efd-panel-close {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #a0aec0;
  cursor: pointer;
  padding: 0.2rem;
  line-height: 1;
  flex-shrink: 0;
}

.efd-panel-close:hover { color: #2d3748; }

.efd-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

/* Rule rows */
.efd-rule-row {
  padding: 0.875rem;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.efd-rule-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.efd-rule-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2d3748;
}

.efd-rule-type-badge {
  font-size: 0.68rem;
  background: #edf2f7;
  color: #4a5568;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  font-weight: 500;
}

.efd-bool {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
}

.efd-bool--yes { background: rgba(72,187,120,.12); color: #276749; }
.efd-bool--no  { background: rgba(229,62,62,.1);   color: #c53030; }

.efd-value { font-size: 0.875rem; color: #2d3748; }
.efd-null  { font-size: 0.875rem; color: #a0aec0; }

.efd-comment {
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #718096;
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
}

/* Photo */
.efd-photo-area { display: flex; flex-direction: column; gap: 0.5rem; }

.efd-photo-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: 1px solid #4299e1;
  color: #3182ce;
  border-radius: 6px;
  padding: 0.35rem 0.8rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
}

.efd-photo-btn:hover { background: #ebf8ff; }
.efd-photo-btn:disabled { opacity: 0.6; cursor: default; }

.efd-photo-img {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
  max-height: 260px;
  border: 1px solid #edf2f7;
}

.efd-no-rules { font-size: 0.875rem; color: #a0aec0; text-align: center; padding: 1.5rem 0; }

.efd-detail-loading { padding: 0.5rem 0; }

/* Slide transition */
.efd-slide-enter-active, .efd-slide-leave-active {
  transition: opacity 0.2s ease;
}

.efd-slide-enter-active .efd-panel,
.efd-slide-leave-active .efd-panel {
  transition: transform 0.25s ease;
}

.efd-slide-enter .efd-panel,
.efd-slide-leave-to .efd-panel {
  transform: translateX(100%);
}

.efd-slide-enter,
.efd-slide-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/views/ExecutionFocusDashboard.vue